import { useEffect, useState } from "react";
import { Download, FileBarChart, Loader2, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader, PageHeader } from "@/components/common";
import { BriefEmailStatus } from "@/components/BriefEmailStatus";
import { useAuth } from "@/context/AuthContext";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

const BRIEF_COPY = {
  competitor: { prefix: "competitor-brief", heading: "Competitor brief (PDF)", blurb: "Meta Muse (muse.ai) and how it compares with The Queue, with the at-a-glance chart. Confidential." },
  combined: { prefix: "combined-brief", heading: "Combined competitor brief (PDF)", blurb: "The Queue vs Cozi, FamilyWall, OurFamilyWizard and Meta Muse, with the side-by-side chart and pricing math. Confidential." },
};

export default function AdminCompetitorBrief({ slug = "competitor" }) {
  const { prefix, heading, blurb } = BRIEF_COPY[slug];
  const base = `/admin/briefs/${slug}`;
  const { session } = useAuth();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { setM(null); api.get(base).then(({ data }) => setM(data)).catch((x) => setErr(errMsg(x))); }, [base]);
  if (session?.user?.platform_role !== "super_admin") return <p className="text-q-muted" data-testid={`${prefix}-forbidden`}>Only the super admin can open the briefs.</p>;
  if (err) return <p className="text-red-300" role="alert" data-testid={`${prefix}-error`}>{err}</p>;
  if (!m) return <Loader />;
  const run = async (kind, fn) => { setBusy(kind); try { await fn(); } catch (x) { toast.error(errMsg(x)); api.get(base).then(({ data }) => setM(data)); } finally { setBusy(null); } };
  const download = () => run("dl", async () => {
    const { data } = await api.get(`${base}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = m.filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  });
  const regen = () => run("regen", async () => { setM((await api.post(`${base}/regenerate`)).data); toast.success("Brief rebuilt"); });
  const email = () => run("email", async () => { const { data } = await api.post(`${base}/email`); setM(data); toast.success(data.email?.message || "Sent"); });
  return (
    <div data-testid={`${prefix}-page`}>
      <PageHeader eyebrow="Admin · Briefs" title={heading} rgb="var(--glow-admin)" testid={`${prefix}-title`} />
      <section className="q-card p-5">
        <div className="flex items-start gap-4">
          <FileBarChart className="mt-1 h-8 w-8 shrink-0 text-violet-300" />
          <div className="min-w-0">
            <p className="font-semibold text-q-text">{m.title}</p>
            <p className="text-sm text-q-muted">{blurb}</p>
            <p className="mt-2 text-sm text-q-faint" data-testid={`${prefix}-meta`}>
              {m.ready ? `Generated ${when(m.generated_at)} · ${m.pages} pages · ${kb(m.size)}` : "Not generated yet."}
            </p>
            <BriefEmailStatus e={m.email} onDownload={download} prefix={prefix} />
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <button className="q-btn q-btn-primary min-h-[44px]" onClick={download} disabled={!m.ready || !!busy} data-testid={`${prefix}-download-btn`}>
            {busy === "dl" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download
          </button>
          <button className="q-btn q-btn-ghost min-h-[44px]" onClick={email} disabled={!m.ready || !!busy || !m.recipient} data-testid={`${prefix}-email-btn`}>
            {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Email to {m.recipient || "—"}
          </button>
          <button className="q-btn q-btn-secondary min-h-[44px]" onClick={regen} disabled={!!busy} data-testid={`${prefix}-regenerate-btn`}>
            {busy === "regen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Regenerate
          </button>
        </div>
      </section>
    </div>
  );
}
